/**
 * Deriving a check-in field's machine `key`, and a dropdown option's machine
 * `value`, from English text.
 *
 * # Why this exists
 *
 * `VisitorCheckInField.key` is the name a collected answer is stored and reported
 * under, and it is immutable after creation — renaming it would orphan every value
 * already gathered. That makes it load-bearing, and it also makes it an
 * implementation detail no admin should ever have been asked to type. This module
 * is the whole of "type an English label, get a key".
 *
 * # Why it lives in `contracts`
 *
 * Both ends need it and neither may own it. The server derives the key it actually
 * stores; the console shows a live preview of what it is about to get. One
 * implementation, imported by both, is the only version of that which cannot drift.
 * Dependency-free, like the rest of this package (docs/002).
 *
 * # Why the option value lives here too
 *
 * A dropdown option's `value` is the same idea one level down: it is what a
 * visitor's answer is stored as, it is immutable once an answer exists, and it is
 * likewise not something an admin should ever have been asked to type. Sharing
 * {@link normalizeToSlug} with the field key is deliberate — two normalisers would
 * eventually disagree about what `"Numéro"` becomes, and the two strings end up in
 * the same exported report.
 */
/**
 * The shape the database column and `createCheckInFieldSchema` both hold a key to.
 * Lives HERE rather than inline next to the schema so `deriveFieldKey` below and
 * the schema that validates an explicitly-supplied key cannot disagree — they are
 * the same regex or the derivation would be able to emit a key the schema refuses.
 */
export const FIELD_KEY_PATTERN = /^[a-z](?:[a-z0-9_]*[a-z0-9])?$/;
/** The column bound. A derived key is truncated to fit rather than refused. */
export const MAX_FIELD_KEY_LENGTH = 64;
/**
 * The key a label yields when it contains nothing a key can be built from — a label
 * written entirely in a non-Latin script, or in punctuation. Never a 400: "客人姓名"
 * is a legitimate label, and refusing it would make the derived-key feature a
 * Latin-alphabet-only feature. The caller's collision loop disambiguates the second
 * such field to `field_2`, which is exactly what it does for two identical labels.
 */
const FALLBACK_FIELD_KEY = 'field';
/**
 * A key that starts with a digit is prefixed rather than truncated to its first
 * letter: `2nd Visitor` should read as `f_2nd_visitor`, not `nd_visitor`.
 */
const LEADING_DIGIT_PREFIX = 'f_';
/**
 * `"Badge Number"` -> `badge_number`. `"Numéro de badge"` -> `numero_de_badge`.
 * `"2nd host"` -> `f_2nd_host`. `"客人姓名"` -> `field`.
 *
 * Always returns a string matching {@link FIELD_KEY_PATTERN} and no longer than
 * {@link MAX_FIELD_KEY_LENGTH} — total by construction, so a caller never has to
 * handle "the label had no key in it" as a separate branch.
 */
export function deriveFieldKey(englishLabel) {
    const slug = normalizeToSlug(englishLabel);
    if (slug === '')
        return FALLBACK_FIELD_KEY;
    const prefixed = /^[a-z]/.test(slug) ? slug : `${LEADING_DIGIT_PREFIX}${slug}`;
    return fitSlug(prefixed, MAX_FIELD_KEY_LENGTH, FALLBACK_FIELD_KEY);
}
/**
 * The nth candidate for `base`: `badge_number`, `badge_number_2`, `badge_number_3`.
 *
 * `attempt` is 1-based and 1 returns `base` unchanged, so a caller's retry loop is
 * `suffixFieldKey(base, attempt)` on every pass with no special case for the first.
 *
 * THE BASE IS SHORTENED SO THE SUFFIX FITS. Appending `_2` to a key already at the
 * 64-character bound would produce a 66-character key that the column and the
 * pattern both refuse — a bug that only appears for a long label whose key is
 * already taken, i.e. never in a test written against short labels.
 */
export function suffixFieldKey(base, attempt) {
    if (attempt <= 1)
        return base;
    const suffix = `_${String(attempt)}`;
    const head = trimUnderscores(base.slice(0, MAX_FIELD_KEY_LENGTH - suffix.length));
    return `${head === '' ? FALLBACK_FIELD_KEY : head}${suffix}`;
}
function trimUnderscores(value) {
    return value.replace(/^_+/, '').replace(/_+$/, '');
}
/** The column bound on a stored option `value` (`fieldOptionSchema`). */
export const MAX_OPTION_VALUE_LENGTH = 80;
/**
 * What an option label yields when it holds nothing a slug can be built from —
 * the same "never a 400" reasoning as {@link deriveFieldKey}'s own fallback: an
 * option reading "是" is a legitimate choice, and refusing it would make derived
 * values a Latin-alphabet-only feature. The caller's collision loop disambiguates
 * the second such option to `option_2`.
 */
const FALLBACK_OPTION_VALUE = 'option';
/**
 * `"Yes"` -> `yes`. `"No thanks"` -> `no_thanks`. `"是"` -> `option`.
 *
 * Always returns a non-empty string no longer than {@link MAX_OPTION_VALUE_LENGTH},
 * so a caller never has to handle "the label had no value in it" as a branch.
 *
 * DELIBERATELY THE SAME GRAMMAR AS A FIELD KEY, one bound wider. An option value is
 * read by a human exactly once — in an exported answer report — and `no_thanks`
 * next to `badge_number` reads as one system, where a second spelling would not.
 */
export function deriveOptionValue(englishLabel) {
    return fitSlug(normalizeToSlug(englishLabel), MAX_OPTION_VALUE_LENGTH, FALLBACK_OPTION_VALUE);
}
/**
 * The nth candidate for `base`: `yes`, `yes_2`, `yes_3` — {@link suffixFieldKey}'s
 * contract, against the option bound.
 *
 * Needed even though duplicate option LABELS are refused outright
 * (`DUPLICATE_OPTION_LABEL`, `check-in-flow.ts`): two DIFFERENT labels can still
 * normalise to one slug — `"Yes!"` and `"Yes?"` are distinct choices a visitor can
 * tell apart, and both derive `yes`. Refusing that pair would be refusing a
 * legitimate dropdown; suffixing keeps the stored answers distinguishable, which is
 * the only thing `value` has to guarantee.
 */
export function suffixOptionValue(base, attempt) {
    if (attempt <= 1)
        return base;
    const suffix = `_${String(attempt)}`;
    const head = trimUnderscores(base.slice(0, MAX_OPTION_VALUE_LENGTH - suffix.length));
    return `${head === '' ? FALLBACK_OPTION_VALUE : head}${suffix}`;
}
/**
 * Assigns every option a unique machine value, in order, honouring any value the
 * caller already has.
 *
 * `value` present is passed through UNCHANGED — that is how an option keeps the
 * value its already-collected answers are stored under across an edit, and how
 * tenant provisioning states the values its seeded choices are reported under. Only
 * the `undefined` ones are derived, and they are derived AROUND the explicit ones:
 * the taken-set is seeded with every supplied value first, so a new option labelled
 * "Yes" next to an existing option already stored as `yes` becomes `yes_2` rather
 * than silently colliding with it.
 *
 * THE WHOLE POINT IS THAT IT IS TOTAL AND DETERMINISTIC: the admin console shows a
 * preview of what the server is about to store, and the server stores it, from this
 * one function over the same input — so the preview cannot be a lie. A per-option
 * derivation with the collision loop written separately at each call site is how
 * those two drift apart.
 */
export function deriveOptionValues(options) {
    const taken = new Set(options.map((option) => option.value).filter((value) => value !== undefined));
    return options.map((option) => {
        if (option.value !== undefined)
            return option.value;
        const base = deriveOptionValue(option.label);
        for (let attempt = 1;; attempt += 1) {
            const candidate = suffixOptionValue(base, attempt);
            if (!taken.has(candidate)) {
                taken.add(candidate);
                return candidate;
            }
        }
    });
}
/**
 * The shared normalisation behind both derivations: strip accents to their base
 * letter, lowercase, collapse everything else to `_`, and trim the edges.
 *
 * Returns `''` for text holding nothing usable. DOES NOT TRUNCATE — a field key
 * prefixes a leading digit with `f_` and must do so BEFORE the cut or a 64-character
 * slug becomes a 66-character key, so the bound is {@link fitSlug}'s job and each
 * caller applies it at the right moment.
 */
function normalizeToSlug(text) {
    const ascii = text
        // NFKD splits an accented character into a base letter plus a combining mark,
        // which the next step then drops — so `é` survives as `e` instead of being
        // erased with the rest of the non-ASCII. Without this, `Numéro` slugs as
        // `num_ro`.
        .normalize('NFKD')
        .replace(/\p{M}+/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_');
    return trimUnderscores(ascii);
}
/**
 * Cuts `slug` to `max` and guarantees a non-empty result.
 *
 * The trim runs AFTER the cut, not only before it: truncation can leave a trailing
 * `_`, which {@link FIELD_KEY_PATTERN} refuses — a bug that only appears for a label
 * long enough to be cut, i.e. never in a test written against short labels.
 */
function fitSlug(slug, max, fallback) {
    const cut = trimUnderscores(slug.slice(0, max));
    return cut === '' ? fallback : cut;
}
//# sourceMappingURL=field-key.js.map